<template>
  <div style="padding: 0.75rem;">
    <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1.25rem;">
      <div>
        <h2 style="font-size: 1.35rem; font-weight: 600; margin: 0 0 0.25rem;">Marketing</h2>
        <p class="text-muted" style="margin: 0; font-size: 0.85rem;">Documentos, catálogos, apresentações e materiais de apoio</p>
      </div>
      <b-button v-if="isAdmin" variant="primary" size="sm" @click="openUploadModal">
        <feather-icon icon="UploadCloudIcon" size="14" class="mr-25" />
        Novo Material
      </b-button>
    </div>

    <div style="display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1rem; align-items: center;">
      <b-form-input
        v-model="searchQuery"
        placeholder="Buscar materiais..."
        size="sm"
        style="max-width: 280px;"
        debounce="400"
        @update="loadMaterials"
      />
      <b-form-select v-model="filterCategoria" size="sm" style="max-width: 180px;" @change="loadMaterials">
        <option value="">Todas as categorias</option>
        <option value="catalogo">Catálogos</option>
        <option value="apresentacao">Apresentações</option>
        <option value="documento">Documentos</option>
        <option value="imagem">Imagens</option>
        <option value="video">Vídeos</option>
        <option value="outro">Outros</option>
      </b-form-select>
      <span class="text-muted" style="font-size: 0.8rem; margin-left: auto;">{{ total }} material(is)</span>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <div v-if="!loading && items.length === 0" class="text-center py-5">
      <feather-icon icon="FolderIcon" size="48" class="text-muted mb-1" />
      <h5 class="text-muted">Nenhum material encontrado</h5>
      <p v-if="isAdmin" class="text-muted" style="font-size: 0.85rem;">Clique em "Novo Material" para adicionar documentos, catálogos e apresentações.</p>
    </div>

    <div v-if="!loading && items.length > 0" class="materials-grid">
      <div
        v-for="item in items"
        :key="item.id"
        class="material-card"
      >
        <div class="material-icon-area" :class="'cat-' + item.categoria">
          <feather-icon :icon="categoryIcon(item.categoria)" size="28" />
        </div>
        <div class="material-info">
          <h6 class="material-title">{{ item.titulo }}</h6>
          <p v-if="item.descricao" class="material-desc">{{ item.descricao }}</p>
          <div class="material-meta">
            <b-badge :variant="categoryVariant(item.categoria)" pill size="sm">{{ categoryLabel(item.categoria) }}</b-badge>
            <span class="meta-item">
              <feather-icon icon="HardDriveIcon" size="11" />
              {{ formatSize(item.file_size) }}
            </span>
            <span class="meta-item">
              <feather-icon icon="DownloadIcon" size="11" />
              {{ item.download_count }}
            </span>
          </div>
          <div class="material-footer">
            <span class="text-muted" style="font-size: 0.7rem;">{{ formatDate(item.created_at) }}</span>
            <span v-if="item.uploaded_by_name" class="text-muted" style="font-size: 0.7rem;">por {{ item.uploaded_by_name }}</span>
          </div>
        </div>
        <div class="material-actions">
          <b-button variant="primary" size="sm" @click="downloadMaterial(item)" title="Baixar">
            <feather-icon icon="DownloadIcon" size="14" />
          </b-button>
          <b-button v-if="isAdmin" variant="flat-secondary" size="sm" @click="editMaterial(item)" title="Editar">
            <feather-icon icon="EditIcon" size="14" />
          </b-button>
          <b-button v-if="isAdmin" variant="flat-danger" size="sm" @click="deleteMaterial(item)" title="Excluir">
            <feather-icon icon="Trash2Icon" size="14" />
          </b-button>
        </div>
      </div>
    </div>

    <div v-if="total > perPage" style="display: flex; justify-content: flex-end; margin-top: 1rem;">
      <b-pagination v-model="currentPage" :total-rows="total" :per-page="perPage" size="sm" @change="onPageChange" />
    </div>

    <b-modal
      v-model="showUploadModal"
      :title="editingItem ? 'Editar Material' : 'Novo Material'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      @ok="saveMaterial"
      :ok-disabled="!canSave"
      centered size="lg"
      no-close-on-backdrop
    >
      <b-row>
        <b-col md="8">
          <b-form-group label="Título *" label-for="mkt-title">
            <b-form-input id="mkt-title" v-model="form.titulo" placeholder="Nome do material" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Categoria" label-for="mkt-cat">
            <b-form-select id="mkt-cat" v-model="form.categoria">
              <option value="catalogo">Catálogo</option>
              <option value="apresentacao">Apresentação</option>
              <option value="documento">Documento</option>
              <option value="imagem">Imagem</option>
              <option value="video">Vídeo</option>
              <option value="outro">Outro</option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="Descrição" label-for="mkt-desc">
        <b-form-textarea id="mkt-desc" v-model="form.descricao" rows="2" placeholder="Descrição breve do material (opcional)" />
      </b-form-group>
      <b-row>
        <b-col md="6">
          <b-form-group label="Visibilidade" label-for="mkt-vis">
            <b-form-select id="mkt-vis" v-model="form.visibilidade">
              <option value="todos">Todos (incluindo clientes)</option>
              <option value="internos">Apenas equipe interna</option>
              <option value="admin">Apenas administradores</option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6" v-if="!editingItem">
          <b-form-group label="Arquivo *" label-for="mkt-file">
            <b-form-file
              id="mkt-file"
              v-model="form.file"
              placeholder="Selecione um arquivo..."
              drop-placeholder="Solte o arquivo aqui..."
              :accept="acceptedTypes"
              browse-text="Procurar"
            />
            <small class="text-muted">Máx. 50MB. Formatos: PDF, Word, Excel, PowerPoint, imagens, vídeos, ZIP.</small>
          </b-form-group>
        </b-col>
      </b-row>
      <div v-if="uploading" class="text-center py-2">
        <b-spinner small variant="primary" class="mr-50" />
        <span>Enviando arquivo...</span>
      </div>
    </b-modal>

    <b-modal
      v-model="showDeleteConfirm"
      title="Excluir Material"
      ok-title="Excluir"
      ok-variant="danger"
      cancel-title="Cancelar"
      centered
      @ok="confirmDelete"
    >
      <p>Tem certeza que deseja excluir o material <strong>{{ deletingItem ? deletingItem.titulo : '' }}</strong>?</p>
      <p class="text-muted" style="font-size: 0.85rem;">Esta ação não pode ser desfeita.</p>
    </b-modal>
  </div>
</template>

<script>
import marketingClient from '@/api/CRM/marketing.client';
import {
  BButton, BSpinner, BRow, BCol, BFormGroup, BBadge,
  BModal, BFormInput, BFormTextarea, BFormSelect, BFormFile,
  BPagination,
} from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'MarketingPage',
  components: {
    BButton, BSpinner, BRow, BCol, BFormGroup, BBadge,
    BModal, BFormInput, BFormTextarea, BFormSelect, BFormFile,
    BPagination,
  },
  data() {
    return {
      loading: true,
      items: [],
      total: 0,
      currentPage: 1,
      perPage: 20,
      searchQuery: '',
      filterCategoria: '',
      showUploadModal: false,
      showDeleteConfirm: false,
      editingItem: null,
      deletingItem: null,
      uploading: false,
      form: {
        titulo: '',
        descricao: '',
        categoria: 'documento',
        visibilidade: 'todos',
        file: null,
      },
      acceptedTypes: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.mov,.avi,.zip,.rar,.txt,.csv',
    };
  },
  computed: {
    isAdmin() {
      const perfil = JSON.parse(localStorage.getItem('perfilLogado') || '{}');
      const nome = perfil.perfil_acesso_nome || '';
      return ['AdminDKP', 'AdminHanes'].includes(nome);
    },
    canSave() {
      if (this.uploading) return false;
      if (!this.form.titulo) return false;
      if (!this.editingItem && !this.form.file) return false;
      return true;
    },
  },
  created() {
    this.loadMaterials();
  },
  methods: {
    async loadMaterials() {
      this.loading = true;
      try {
        const params = { page: this.currentPage, limit: this.perPage };
        if (this.searchQuery) params.search = this.searchQuery;
        if (this.filterCategoria) params.categoria = this.filterCategoria;
        const result = await marketingClient.list(params);
        this.items = result.items || [];
        this.total = result.total || 0;
      } catch (err) {
        console.error('Erro ao carregar materiais:', err);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar materiais',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadMaterials();
    },
    openUploadModal() {
      this.editingItem = null;
      this.form = { titulo: '', descricao: '', categoria: 'documento', visibilidade: 'todos', file: null };
      this.showUploadModal = true;
    },
    editMaterial(item) {
      this.editingItem = item;
      this.form = {
        titulo: item.titulo,
        descricao: item.descricao || '',
        categoria: item.categoria,
        visibilidade: item.visibilidade,
        file: null,
      };
      this.showUploadModal = true;
    },
    async saveMaterial(e) {
      e.preventDefault();
      this.uploading = true;
      try {
        if (this.editingItem) {
          await marketingClient.update(this.editingItem.id, {
            titulo: this.form.titulo,
            descricao: this.form.descricao,
            categoria: this.form.categoria,
            visibilidade: this.form.visibilidade,
          });
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Material atualizado',
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          });
        } else {
          const formData = new FormData();
          formData.append('titulo', this.form.titulo);
          formData.append('descricao', this.form.descricao || '');
          formData.append('categoria', this.form.categoria);
          formData.append('visibilidade', this.form.visibilidade);
          formData.append('file', this.form.file);
          await marketingClient.upload(formData);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Material criado',
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          });
        }
        this.showUploadModal = false;
        this.loadMaterials();
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Erro ao salvar';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: msg,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.uploading = false;
      }
    },
    async downloadMaterial(item) {
      try {
        const response = await this.$http.get(`/marketing/${item.id}/download`, {
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', item.file_original_name || 'download');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        item.download_count = (item.download_count || 0) + 1;
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao baixar material',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    deleteMaterial(item) {
      this.deletingItem = item;
      this.showDeleteConfirm = true;
    },
    async confirmDelete() {
      if (!this.deletingItem) return;
      try {
        await marketingClient.delete(this.deletingItem.id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Material excluído',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
        this.loadMaterials();
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao excluir material',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    categoryIcon(cat) {
      const map = {
        catalogo: 'BookOpenIcon',
        apresentacao: 'MonitorIcon',
        documento: 'FileTextIcon',
        imagem: 'ImageIcon',
        video: 'VideoIcon',
        outro: 'FileIcon',
      };
      return map[cat] || 'FileIcon';
    },
    categoryLabel(cat) {
      const map = {
        catalogo: 'Catálogo',
        apresentacao: 'Apresentação',
        documento: 'Documento',
        imagem: 'Imagem',
        video: 'Vídeo',
        outro: 'Outro',
      };
      return map[cat] || cat;
    },
    categoryVariant(cat) {
      const map = {
        catalogo: 'light-primary',
        apresentacao: 'light-warning',
        documento: 'light-info',
        imagem: 'light-success',
        video: 'light-danger',
        outro: 'light-secondary',
      };
      return map[cat] || 'light-secondary';
    },
    formatSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.materials-grid {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.material-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, #ebe9f1);
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(34,41,47,0.04);
  transition: box-shadow 0.15s, border-color 0.15s;
}
.material-card:hover {
  box-shadow: 0 4px 12px rgba(34,41,47,0.08);
  border-color: #d4d1ed;
}
.material-icon-area {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.cat-catalogo { background: rgba(37,99,235,0.1); color: #2563eb; }
.cat-apresentacao { background: rgba(245,158,11,0.1); color: #f59e0b; }
.cat-documento { background: rgba(14,165,233,0.1); color: #0ea5e9; }
.cat-imagem { background: rgba(22,163,74,0.1); color: #16a34a; }
.cat-video { background: rgba(220,38,38,0.1); color: #dc2626; }
.cat-outro { background: rgba(130,134,139,0.1); color: #82868b; }
.material-info {
  flex: 1;
  min-width: 0;
}
.material-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-heading, #5e5873);
  margin: 0 0 0.15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.material-desc {
  font-size: 0.775rem;
  color: var(--text-muted, #b9b9c3);
  margin: 0 0 0.35rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.material-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.7rem;
  color: var(--text-muted, #b9b9c3);
}
.material-footer {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}
.material-actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .material-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  .material-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>

<style>
body.dark-layout .material-card {
  background: var(--dark-card-bg, #283046) !important;
  border-color: var(--dark-border, #3b4253) !important;
}
body.dark-layout .material-card:hover {
  border-color: #4b4f58 !important;
}
body.dark-layout .material-title {
  color: var(--dark-text-heading, #d0d2d6) !important;
}
body.dark-layout .material-desc {
  color: var(--dark-text-muted, #676d7d) !important;
}
</style>
